import { Link } from "react-router-dom";
import { Mail, ArrowRight, Apple, Play } from "lucide-react";
import { BRAND, SUPPORT_EMAIL, STORE_LINKS } from "@/config";

const StoreBadge = ({ store, href, testid }) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testid}
        className="flex items-center gap-3 rounded-full border border-white/15 bg-[#0b1422] px-5 py-3 transition-colors hover:border-[#3fcbf3]/50"
    >
        {store === "apple" ? (
            <Apple className="h-6 w-6 text-white" fill="currentColor" />
        ) : (
            <Play className="h-5 w-5 text-white" fill="currentColor" />
        )}
        <span className="text-left leading-tight">
            <span className="block text-[10px] uppercase tracking-widest text-[#7d8da3]">
                {store === "apple" ? "Download on the" : "Get it on"}
            </span>
            <span className="block text-sm font-bold text-white">
                {store === "apple" ? "App Store" : "Google Play"}
            </span>
        </span>
    </a>
);

export const StoreBadges = ({ testidPrefix = "store" }) => {
    if (!STORE_LINKS.apple && !STORE_LINKS.google) return null;
    return (
        <div className="flex flex-col gap-3 sm:flex-row">
            {STORE_LINKS.apple && (
                <StoreBadge store="apple" href={STORE_LINKS.apple} testid={`${testidPrefix}-appstore-badge`} />
            )}
            {STORE_LINKS.google && (
                <StoreBadge store="google" href={STORE_LINKS.google} testid={`${testidPrefix}-googleplay-badge`} />
            )}
        </div>
    );
};

const Footer = () => (
    <footer data-testid="site-footer" className="border-t border-white/5 bg-[#050a12]">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 sm:px-8 md:grid-cols-2 lg:grid-cols-4">
            <div>
                <img src={BRAND.logoFooter} alt="SHREDxpro" className="h-24 w-auto" />
                <p className="mt-4 text-sm leading-relaxed text-[#7d8da3]">
                    Your personalized fitness system. Built for people who want real, measurable change.
                </p>
                <div className="mt-6">
                    <StoreBadges testidPrefix="footer" />
                </div>
                <Link to="/trial" data-testid="footer-trial-cta" className="btn-primary mt-6 !px-6 !py-3 !text-xs">
                    Start 7-Day Free Trial <ArrowRight className="h-4 w-4" />
                </Link>
            </div>
            <div>
                <p className="eyebrow">Product</p>
                <ul className="mt-5 space-y-3 text-sm text-[#93a3ba]">
                    <li><Link data-testid="footer-link-features" to="/#showcase" className="transition-colors hover:text-white">Inside SHREDxpro</Link></li>
                    <li><Link data-testid="footer-link-membership" to="/#membership" className="transition-colors hover:text-white">Membership</Link></li>
                    <li><Link data-testid="footer-link-faq" to="/#faq" className="transition-colors hover:text-white">FAQ</Link></li>
                    <li><Link data-testid="footer-link-resources" to="/resources" className="transition-colors hover:text-white">Free Resources</Link></li>
                </ul>
            </div>
            <div>
                <p className="eyebrow">Legal</p>
                <ul className="mt-5 space-y-3 text-sm text-[#93a3ba]">
                    <li><Link data-testid="footer-link-privacy" to="/privacy" className="transition-colors hover:text-white">Privacy Policy</Link></li>
                    <li><Link data-testid="footer-link-terms" to="/terms" className="transition-colors hover:text-white">Terms of Service</Link></li>
                    <li><Link data-testid="footer-link-account-deletion" to="/account-deletion" className="transition-colors hover:text-white">Account Deletion</Link></li>
                </ul>
            </div>
            <div>
                <p className="eyebrow">Support</p>
                <ul className="mt-5 space-y-3 text-sm text-[#93a3ba]">
                    <li><Link data-testid="footer-link-support" to="/support" className="transition-colors hover:text-white">Contact Support</Link></li>
                    <li>
                        <a
                            data-testid="footer-link-email"
                            href={`mailto:${SUPPORT_EMAIL}`}
                            className="inline-flex items-center gap-2 transition-colors hover:text-white"
                        >
                            <Mail className="h-4 w-4" /> {SUPPORT_EMAIL}
                        </a>
                    </li>
                </ul>
            </div>
        </div>
        <div className="border-t border-white/5">
            <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-2 px-5 py-6 text-xs text-[#5c6b82] sm:px-8 md:flex-row md:items-center">
                <span>© 2026 SHREDxpro. All rights reserved.</span>
                <span>7-day free trial · Full access · Cancel anytime</span>
            </div>
        </div>
    </footer>
);

export default Footer;
